import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  InputGroup,
  InputRightAddon,
  InputLeftAddon,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";

const termSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  escrowReserveRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Escrow rate is required")
    .test('sum-with-purchase', 'Sum of rates cannot exceed 100%', function(value) {
      const purchaseRate = this.parent.purchaseRate;
      return !purchaseRate || !value || (Number(purchaseRate) + Number(value)) <= 100;
    }),
  advanceRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Advance rate is required")
    .test('advance-rate-validation', 'Advance rate must equal 100% - purchase rate - escrow rate', 
      function(value) {
        const { purchaseRate, escrowReserveRate } = this.parent;
        return value === 100 - Number(purchaseRate) - Number(escrowReserveRate);
      }),
  minimumPurchaseFee: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .required("Minimum purchase fee is required"),
  purchaseRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Purchase rate is required"),
  feePerInvoice: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .required("Fee per invoice is required"),
});

const CreateTermsModal = ({ isOpen, onClose, onSubmit }) => {
  const formik = useFormik({
    initialValues: {
      title: "",
      escrowReserveRate: "",
      advanceRate: "",
      minimumPurchaseFee: "",
      purchaseRate: "",
      feePerInvoice: "",
    },
    validationSchema: termSchema,
    onSubmit: async (values) => {
      try {
        await onSubmit({
          ...values,
          escrowReserveRate: Number(values.escrowReserveRate),
          advanceRate: Number(values.advanceRate),
          minimumPurchaseFee: Number(values.minimumPurchaseFee),
          purchaseRate: Number(values.purchaseRate),
          feePerInvoice: Number(values.feePerInvoice),
        });
        formik.resetForm();
        onClose();
      } catch (error) {
        console.error("Error creating terms:", error);
      }
    },
  });

  // Add effect to calculate advance rate
  React.useEffect(() => {
    const purchaseRate = Number(formik.values.purchaseRate) || 0;
    const escrowRate = Number(formik.values.escrowReserveRate) || 0;
    const advanceRate = 100 - purchaseRate - escrowRate;
    
    if (!isNaN(advanceRate)) {
      formik.setFieldValue('advanceRate', advanceRate.toString());
    }
  }, [formik.values.purchaseRate, formik.values.escrowReserveRate]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader sx={{ fontSize: "2rem" }}>Create Terms</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6}>
            <FormControl
              isRequired
              isInvalid={formik.touched.title && formik.errors.title}
            >
              <FormLabel>Title</FormLabel>
              <Input
                name="title"
                placeholder="Enter term title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
            </FormControl>

            <HStack spacing={4} width="100%">
              <FormControl
                isRequired
                isInvalid={
                  formik.touched.purchaseRate && formik.errors.purchaseRate
                }
              >
                <FormLabel>Purchase Rate</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="%" />
                  <Input
                    name="purchaseRate"
                    type="number"
                    value={formik.values.purchaseRate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                <FormErrorMessage>
                  {formik.errors.purchaseRate}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isRequired
                isInvalid={
                  formik.touched.escrowReserveRate &&
                  formik.errors.escrowReserveRate
                }
              >
                <FormLabel>Escrow Rate</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="%" />
                  <Input
                    name="escrowReserveRate"
                    type="number"
                    value={formik.values.escrowReserveRate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                <FormErrorMessage>
                  {formik.errors.escrowReserveRate}
                </FormErrorMessage>
              </FormControl>

              
            </HStack>

            <HStack spacing={4} width="100%">
              <FormControl
                isRequired
                isInvalid={
                  formik.touched.feePerInvoice && formik.errors.feePerInvoice
                }
              >
                <FormLabel>Fee per Invoice</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="$" />
                  <Input
                    name="feePerInvoice"
                    type="number"
                    value={formik.values.feePerInvoice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                <FormErrorMessage>
                  {formik.errors.feePerInvoice}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isRequired
                isInvalid={
                  formik.touched.minimumPurchaseFee &&
                  formik.errors.minimumPurchaseFee
                }
              >
                <FormLabel>Minimum Purchase Fee</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="$" />
                  <Input
                    name="minimumPurchaseFee"
                    type="number"
                    value={formik.values.minimumPurchaseFee}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                <FormErrorMessage>
                  {formik.errors.minimumPurchaseFee}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack spacing={4} width="100%">
              <FormControl
                isRequired
                isInvalid={
                  formik.touched.advanceRate && formik.errors.advanceRate
                }
              >
                <FormLabel>Advance Rate</FormLabel>
                <InputGroup>
                  <InputLeftAddon children="%" />
                  <Input
                    name="advanceRate"
                    type="number"
                    value={formik.values.advanceRate}
                    isReadOnly
                    bg="gray.50"
                  />
                </InputGroup>
                <FormErrorMessage>{formik.errors.advanceRate}</FormErrorMessage>
              </FormControl>
              <FormControl>
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap="1rem">
          <Button
            colorScheme="purple"
            onClick={formik.handleSubmit}
            isLoading={formik.isSubmitting}
            width="90px"
            height="40px"
          >
            Create
          </Button>
          <Button variant="ghost" onClick={onClose} width="76px" height="40px">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTermsModal;
